import { observer } from 'mobx-react-lite';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features/use-enabled-features';
import { useEventMetadataStore } from '../../event-panel-form/event-metadata';
import { EventPanelTab, EventPanelTabConfig } from './event-panel-tabs-config';
import { TabList, Tabs } from '@cad-arc';
import { TabHeader } from './tab-header';
import { getEventPanelTabTooltip } from '../../../common/helpers/get-usx-labels';
import { useIsDexEnabled } from '../../../data-exchange/use-dex-access-guard';

export const TabHeaders = observer(function TabHeaders({
    activeTab,
    tabConfigs,
}: {
    activeTab?: EventPanelTab;
    tabConfigs: EventPanelTabConfig[];
}) {
    const eventMetadata = useEventMetadataStore().state.metadata;
    const enabledFeatures = useEnabledFeatures();
    const { isDexEnabled } = useIsDexEnabled();
    const tabsToShow = tabConfigs.filter((tabConfig) => {
        if (tabConfig.type === 'DEX' && !isDexEnabled) return false;
        if (typeof tabConfig.checkEnabled === 'undefined') return true;
        return tabConfig.checkEnabled(enabledFeatures, eventMetadata);
    });

    // The panel was originally built for 12 tabs. With all conditional tabs enabled,
    // up to 13 tabs can now be visible. Reduce tab button width when we hit the 13-tab
    // maximum so all buttons remain visible within the fixed-width container.
    const tabHeaderWidth = tabsToShow.length > 12 ? '2.4rem' : 'max-content';

    return (
        <Tabs
            loop={false}
            isManual={true}
            value={activeTab}
            style={{
                width: '100%',
                backgroundColor: 'var(--arc-colors-surface-foreground)',
                zIndex: 5,
            }}
        >
            <TabList
                style={{
                    borderBottom: 'none',
                    borderTop: 'solid 1px var(--arc-colors-border-default)',
                    width: '100%',
                    overflowX: 'hidden',
                }}
            >
                {tabsToShow.map((tabConfig) => (
                    <TabHeader
                        key={tabConfig.type}
                        value={tabConfig.type}
                        leadingVisual={tabConfig.icon}
                        tooltip={getEventPanelTabTooltip(
                            tabConfig.name,
                            enabledFeatures.dexNextEnabled
                        )}
                        errorFields={tabConfig.errorFields}
                        notificationTypes={
                            'notificationTypes' in tabConfig
                                ? tabConfig.notificationTypes
                                : undefined
                        }
                        tabHeaderWidth={tabHeaderWidth}
                        notificationsEntityTypes={
                            'notificationsEntityTypes' in tabConfig
                                ? tabConfig.notificationsEntityTypes
                                : undefined
                        }
                    />
                ))}
            </TabList>
        </Tabs>
    );
});
